import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { placePanel } from '../popover/panel-position';
import { SelectOption } from './select.model';

/**
 * Theme-aligned custom select with a styled dropdown panel.
 *
 * Replaces native `<select>` so open-state options match the app design.
 * Supports `ngModel` and `formControlName` via ControlValueAccessor.
 *
 * The panel is `position: fixed` and placed by `placePanel` in `../popover`,
 * shared with both date pickers — the transform trap that module documents
 * applies to every ancestor of this component.
 *
 * @example
 * <app-select
 *   variant="form"
 *   [options]="statusOptions"
 *   [(ngModel)]="statusFilter"
 *   placeholder="Status: All"
 * />
 */
@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor {
  /** Options shown in the dropdown panel. */
  options = input.required<SelectOption[]>();

  /** Text shown when no value is selected. */
  placeholder = input('Select...');

  /** `form` — bordered field; `inline` — borderless header style. */
  variant = input<'form' | 'inline'>('form');

  /** Disables interaction (also set by form control disabled state). */
  disabled = input(false);

  /** Read-only display without opening the panel. */
  readonly = input(false);

  /** Marks the trigger with error styling. */
  invalid = input(false);

  /** Accessible name for the trigger button. */
  ariaLabel = input<string>();

  /** Extra Tailwind classes on the trigger (e.g. width utilities). */
  triggerClass = input('');

  /**
   * Label for an action row pinned below the options, e.g. "New folder".
   *
   * An action rather than an option with a sentinel value: choosing it must not
   * write anything into the bound model, or the form would briefly hold a value
   * that names no real choice. Null hides the row.
   */
  actionLabel = input<string | null>(null);

  /** Material symbol shown beside `actionLabel`. */
  actionIcon = input('add');

  /** Fires when the action row is chosen. The panel closes first. */
  action = output<void>();

  isOpen = signal(false);
  highlightedIndex = signal(-1);

  panelPosition = signal<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  /** True when the panel was flipped above the trigger for lack of room below. */
  flipped = signal(false);

  private value = signal('');
  private cvaDisabled = signal(false);

  triggerRef = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  panelRef = viewChild<ElementRef<HTMLUListElement>>('panel');

  private onScroll = () => this.updatePosition();

  constructor() {
    // Belt and braces: close() detaches, but a component destroyed while its
    // panel is open (route change, dialog teardown) never reaches close().
    inject(DestroyRef).onDestroy(() => this.detachScroll());
  }

  /*
   * Capturing, on document, rather than `window:scroll` via @HostListener:
   * scroll events from an inner scroll container (the assignment dialog body
   * is `overflow-y-auto`) do not bubble to window, so a host listener never
   * fires and the panel silently detaches from its trigger. Capture phase sees
   * them all. Attached only while open — a screen with four selects would
   * otherwise carry four listeners firing on every scroll to do nothing.
   */
  private attachScroll(): void {
    document.addEventListener('scroll', this.onScroll, true);
  }

  private detachScroll(): void {
    document.removeEventListener('scroll', this.onScroll, true);
  }

  isDisabled = computed(
    () => this.disabled() || this.cvaDisabled() || this.readonly(),
  );

  selectedLabel = computed(() => {
    const current = this.value();
    const match = this.options().find((o) => o.value === current);
    if (match) return match.label;
    return this.placeholder();
  });

  hasSelection = computed(() => {
    const current = this.value();
    return current !== '' && this.options().some((o) => o.value === current);
  });

  /** Current value exposed for template bindings. */
  selectedValue = computed(() => this.value());

  triggerClasses = computed(() => {
    const base =
      this.variant() === 'inline'
        ? [
            'bg-transparent border-none text-sm font-medium text-text-primary',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-inset rounded',
            'justify-start py-1.5 md:py-2 pl-2.5 pr-2.5 min-w-0 max-w-full',
          ]
        : [
            'w-full justify-between px-3 py-2 border rounded-lg text-sm text-text-primary bg-white',
            'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary',
            'transition-colors text-left',
          ];

    if (this.variant() === 'form') {
      base.push(
        this.invalid()
          ? 'border-red-400'
          : 'border-border-medium',
      );
      if (this.readonly()) base.push('bg-background-light');
    }

    if (this.isDisabled()) base.push('opacity-60 cursor-not-allowed');
    else base.push('cursor-pointer');

    const extra = this.triggerClass().trim();
    if (extra) base.push(extra);

    return base.join(' ');
  });

  /**
   * Classes for one option row.
   *
   * A method rather than three `[class.x]` bindings: the selected and hover
   * tints are `bg-primary/10` and `hover:bg-primary/5`, and a class binding
   * whose name contains `/` or `:` only works by accident of Tailwind's raw
   * text scanner — `[class.hover:bg-primary/5]` in particular cannot express
   * hover state, since Angular evaluates the condition, not the browser.
   */
  optionClasses(value: string, index: number): string {
    const base = 'w-full flex items-center px-4 py-2.5 text-sm text-left transition-colors';

    if (this.selectedValue() === value) {
      return `${base} text-primary font-medium bg-primary/10`;
    }

    // Built as an array, not one interpolated template literal: Tailwind's
    // scanner reads these files as plain text, and a `${...}` immediately after
    // a utility swallows it into the candidate — `hover:bg-primary/5${x}` is not
    // a class name, so the hover tint was never emitted into the stylesheet.
    const classes = [base, 'text-text-primary', 'hover:bg-primary/5'];
    if (this.highlightedIndex() === index) classes.push('bg-primary/5');
    return classes.join(' ');
  }

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen()) return;

    const opts = this.options();
    // The action row, when shown, is one more stop after the last option.
    const hasAction = this.actionLabel() !== null;
    const count = opts.length + (hasAction ? 1 : 0);
    if (count === 0) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex.update((i) =>
          i < count - 1 ? i + 1 : 0,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex.update((i) =>
          i > 0 ? i - 1 : count - 1,
        );
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        {
          const idx = this.highlightedIndex();
          if (idx >= 0 && idx < opts.length) {
            this.selectOption(opts[idx].value);
          } else if (hasAction && idx === opts.length) {
            this.runAction();
          }
        }
        break;
    }
  }

  toggle(): void {
    if (this.isDisabled()) return;

    if (this.isOpen()) {
      this.close();
      return;
    }

    this.open();
  }

  @HostListener('window:resize', [])
  onWindowResize(): void {
    this.updatePosition();
  }

  /**
   * Pin the fixed-position panel to the trigger.
   *
   * Called on open and again on every scroll/resize while open — the panel is
   * `position: fixed`, so a position captured once drifts away from its
   * trigger the moment anything scrolls.
   *
   * The flip-and-clamp maths is `placePanel`. Flipping above the trigger when
   * there is no room below is what keeps the Frequency and Month selects near
   * the bottom of the assignment dialog on screen.
   */
  private updatePosition(): void {
    if (!this.isOpen()) return;

    const rect = this.triggerRef().nativeElement.getBoundingClientRect();

    // Unlike the date pickers' fixed-width panels, this one tracks its trigger,
    // so the clamp's fallback width is derived rather than a literal. An
    // `inline` trigger is only as wide as its label, hence the 160 floor — the
    // same one the template applies as `min-width`.
    const width =
      this.variant() === 'inline' ? Math.max(rect.width, 160) : rect.width;

    const placement = placePanel(
      this.triggerRef().nativeElement,
      this.panelRef()?.nativeElement,
      // `max-h-60` is the height ceiling, used until the panel is rendered.
      { height: 240, width },
    );

    this.flipped.set(placement.flipped);
    this.panelPosition.set({
      top: placement.top,
      left: placement.left,
      width: rect.width,
    });
  }

  open(): void {
    const currentIdx = this.options().findIndex(
      (o) => o.value === this.value(),
    );
    this.highlightedIndex.set(currentIdx >= 0 ? currentIdx : 0);
    this.isOpen.set(true);
    this.attachScroll();

    // Once before render so the panel never paints at 0,0, then again after it
    // exists so the flip decision can use its real height.
    this.updatePosition();
    requestAnimationFrame(() => this.updatePosition());
  }

  close(): void {
    if (!this.isOpen()) return;
    this.detachScroll();
    this.isOpen.set(false);
    this.highlightedIndex.set(-1);
    this.flipped.set(false);
    this.onTouched();
  }

  selectOption(value: string): void {
    this.value.set(value);
    this.onChange(value);
    this.close();
  }

  /** Same reasoning as `optionClasses` — no `[class.x]` with a `/` in it. */
  actionClasses(): string {
    const classes = [
      'w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left font-medium',
      'text-primary transition-colors hover:bg-primary/5',
    ];
    if (this.highlightedIndex() === this.options().length) {
      classes.push('bg-primary/5');
    }
    return classes.join(' ');
  }

  runAction(): void {
    this.close();
    this.action.emit();
  }

  onBackdropClick(): void {
    this.close();
  }

  onPanelClick(event?: Event): void {
    event?.stopPropagation();
  }
}
